import {
  FeatureAudience,
  FeatureKey,
  FeatureRegistry,
  LEGACY_FEATURE_KEY_ALIASES,
  PlatformAudienceOverrides,
} from './types';
import { FEATURE_REGISTRY } from './registry';
import { getFeature } from './evaluation-helpers';

export function normalizeFeatureKey(value: string): FeatureKey | null {
  if (Object.values(FeatureKey).includes(value as FeatureKey)) {
    return value as FeatureKey;
  }
  return LEGACY_FEATURE_KEY_ALIASES[value] ?? null;
}

export function isAudienceScopableFeature(
  key: FeatureKey,
  registry: FeatureRegistry = FEATURE_REGISTRY,
): boolean {
  return getFeature(key, registry).audienceScopable === true;
}

export function resolveAudienceFeatureState(
  key: FeatureKey,
  audience: FeatureAudience,
  options: {
    registry?: FeatureRegistry;
    globalEnabled: boolean;
    memberEnabled?: boolean | null;
    creatorEnabled?: boolean | null;
  },
): boolean {
  const registry = options.registry ?? FEATURE_REGISTRY;
  const feature = getFeature(key, registry);

  if (!feature.audienceScopable) {
    return options.globalEnabled;
  }

  const audienceOverride =
    audience === FeatureAudience.CREATOR ? options.creatorEnabled : options.memberEnabled;

  if (audienceOverride !== undefined && audienceOverride !== null) {
    return audienceOverride;
  }

  return options.globalEnabled;
}

export function buildAudienceFeatureStateMap(
  audience: FeatureAudience,
  options: {
    registry?: FeatureRegistry;
    globalStates: Readonly<Record<FeatureKey, boolean>>;
    audienceOverrides?: PlatformAudienceOverrides;
  },
): Readonly<Record<FeatureKey, boolean>> {
  const registry = options.registry ?? FEATURE_REGISTRY;

  return Object.values(FeatureKey).reduce<Record<FeatureKey, boolean>>((states, key) => {
    const override = options.audienceOverrides?.[key];
    states[key] = resolveAudienceFeatureState(key, audience, {
      registry,
      globalEnabled: options.globalStates[key] ?? registry[key].defaultState,
      memberEnabled: override?.member,
      creatorEnabled: override?.creator,
    });
    return states;
  }, {} as Record<FeatureKey, boolean>);
}

/**
 * Studio module access uses platform-wide flags for most modules. Audience-scoped
 * modules that are also creator-overrideable (e.g. Circles) resolve platform
 * availability from the creator audience toggle so Admin member/creator controls
 * stay aligned with Studio without treating member-only access as Studio-on.
 */
export function resolveStudioModulePlatformEnabled(
  key: FeatureKey,
  options: {
    registry?: FeatureRegistry;
    globalEnabled: boolean;
    audienceOverrides?: PlatformAudienceOverrides;
  },
): boolean {
  const registry = options.registry ?? FEATURE_REGISTRY;
  const feature = getFeature(key, registry);

  if (feature.audienceScopable && feature.creatorOverrideable) {
    const override = options.audienceOverrides?.[key];
    return resolveAudienceFeatureState(key, FeatureAudience.CREATOR, {
      registry,
      globalEnabled: options.globalEnabled,
      memberEnabled: override?.member,
      creatorEnabled: override?.creator,
    });
  }

  return options.globalEnabled;
}
